import React from 'react';
import List from '@mui/material/List';
import classNames from 'classnames';
import Parser from 'html-react-parser';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import dayjs from 'dayjs';

import { trackingNotificationType } from '../../../constants';
import ListItem from '../../../components/ListItem';
import ViewNotificationItem from '../../../components/Notification/Views/NotificationItem';
import Loading from '../../../components/Loading';
import Card from '../../../components/Card';
import Button from '../../../components/Button';
import ECharts from '../../../components/ECharts';
import CustomScrollbars from '../../../components/CustomScrollbars';
import { setOpenNotificationDrawer } from '../../../components/Notification/NotificationSlice';
import { transferAndGroupNotificationData } from '../../../components/Notification/NotificationDrawer.helpers';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { ROUTES } from '../../../constants/routes';
import * as CardStyles from '../../../components/Card/Card.styles';
import { getICon } from '../../../components/Notification/NotificationDrawer';
import * as NotificationStyles from '../../../components/Notification/Notification.styles';
import * as Styles from '../ManageInvestments.styles';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import useNotification from '../../../hooks/useNotification';
import WrapperBookmark from '../../Dashboard/components/WrapperBookmark';
import useInvestmentCategories from '../../../hooks/useInvestmentCategories';
import { trackingNotificationType } from '../../../constants';
import { formatDisplayNumber } from '../../../utils/format';
import { parseDate } from '../../../utils/date';
import { getFinancialData } from '../../../pages/ManageInvestments/components/FinancialChartModal';
import {
  getMinMax,
  toPlainString,
  getColumnColor,
  getMinMax,
  removeVietnameseTones,
} from '../../../utils/helpers';

export const FinancialChart = ({ financialData, isDarkMode }) => {
  const getChartOptions = () => {
    const {
      xAxisData,
      seriesData,
      tmpProfitData,
    } = getFinancialData(financialData, [
      'lnsauthue',
      'doanhthuthuan',
      'tongnophaitra',
    ]);
    const bgColor = isDarkMode ? '#1a1c23' : '#fff';
    const color = isDarkMode ? '#e5e7eb' : '#24262d';
    const minMax = getMinMax(tmpProfitData);
    let min = minMax[0];
    let max = minMax[1];
    if (minMax[0] < 0) {
      if (min < 0 && max > 0) {
        if (min * -1 > max) {
          max = min * -1;
        } else {
          min = max * -1;
        }
      } else if (min < 0 && max < 0) {
        max = min * -1;
      } else if (min > 0) {
        min = max * -1;
      }
    } else {
      min = 0;
    }
    return {
      backgroundColor: bgColor,
      tooltip: {
        trigger: 'axis',
        formatter(params) {
          let html = '';
          for (let i = 0; i < params.length; i++) {
            html += `
              <div class="flex justify-between w-full">
                <div class="mr-4">${params[i].marker} ${
              params[i].seriesName
            }:</div>
                <div class="font-bold">${formatDisplayNumber(
                  params[i].value
                )} tỷ</div>
              </div>
            `;
          }
          return `
            <div>
              <div class="font-bold">${params[0].axisValue}</div>
              <div>${html}</div>
            </div>
          `;
        },
      },
      grid: {
        left: 70,
        right: 10,
        top: 10,
        bottom: 30,
      },
      xAxis: [
        {
          type: 'category',
          axisTick: {
            show: true,
          },
          data: xAxisData,
          axisLabel: {
            color,
          },
        },
      ],
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            show: true,
            formatter(value) {
              let newValue = parseFloat(
                Number(toPlainString(value)).toFixed(2)
              );
              return `${formatDisplayNumber(newValue, ',', 0)} tỷ`;
            },
            color,
          },
          splitLine: {
            show: false,
          },
          axisLine: {
            show: true,
          },
          axisTick: {
            show: true,
          },
          min,
          max,
          interval: (max - min) / 6,
        },
      ],
      series: seriesData,
    };
  };
  return (
    <div>
      <ECharts options={{ ...getChartOptions() }} height="250px" notMerge />
      <ul className="flex items-center mb-3 flex-wrap sm:flex-nowrap">
        <li className="mr-4 mb-2 sm:mb-0 flex items-center">
          <span
            className="w-3.5 h-3.5 min-w-3.5 rounded-full inline-block mr-1"
            style={{ backgroundColor: '#e1c103' }}
          ></span>
          <span>Doanh thu thuần</span>
        </li>
        <li className="mr-4 mb-2 sm:mb-0 flex items-center">
          <span
            className="w-3.5 h-3.5 min-w-3.5 rounded-full inline-block mr-1"
            style={{ backgroundColor: '#008e00' }}
          ></span>
          <span>Lợi nhuận ròng</span>
        </li>
        <li className="mb-2 sm:mb-0 flex items-center">
          <span
            className="w-3.5 h-3.5 min-w-3.5 rounded-full inline-block mr-1"
            style={{ backgroundColor: '#b30303' }}
          ></span>
          <span>Tổng nợ vay</span>
        </li>
      </ul>
    </div>
  );
};

export const renderContentItem = (items = []) => {
  return items?.map((item, index) => (
    <Box key={index}>{Parser(item.replace(/\n/g, '<br />'))}</Box>
  ));
};

export const renderHighlight = (items = []) => {
  return items?.map((item, index) => (
    <a
      key={index}
      href={`/${item.machungkhoan}${
        item?.name ? '-' + removeVietnameseTones(item?.name) : ''
      }`}
      className="hover:underline transition ease-in-out duration-200 text-primary-main whitespace-normal mr-1 inline-block"
    >
      {item.machungkhoan}
      {index < items.length - 1 ? ',' : ''}
    </a>
  ));
};

export const getTotalFollow = (items = []) => {
  return items.reduce((total, item) => total + item.stocks.length, 0);
};

export const getMinMaxZizZag = (items = []) => {
  const percent = items.map((item) => item.percent);
  const result = getMinMax(percent);
  return result;
};

export const checkStockInInvestment = (stock, investments) => {
  const item = investments.find((i) => i.machungkhoan === stock);
  if (item) {
    return <span>(Đang đầu tư)</span>;
  }
  return null;
};

export const getFollowCompanies = (follows = [], investments = []) => {
  if (!follows?.length) {
    return [];
  }
  const results = [];
  for (const follow of follows) {
    const _stocks = [];
    for (const stock of follow.stocks) {
      if (!checkStockInInvestment(stock?.machungkhoan, investments)) {
        _stocks.push(stock);
      }
    }
    if (_stocks?.length) {
      results.push({
        ...follow,
        stocks: _stocks,
      });
    }
  }

  return results;
};

const RelatedStock = ({ machungkhoan, companies }) => {
  if (!machungkhoan || !companies?.length) {
    return null;
  }
  return machungkhoan.map((stock) => {
    const company = companies.find((c) => c.machungkhoan === stock);
    return (
      <a
        href={`${company?.machungkhoan?.toLowerCase()}-${removeVietnameseTones(
          company?.name
        )}`}
        title={`${company?.name}  | Mã chứng khoán: ${stock}`}
        key={`mlq_${stock}`}
        className="mlq"
      >
        {stock}
      </a>
    );
  });
};

const NotificationItem = ({
  notification,
  listStockShow,
  isInvestmentCategoriesLoading,
  authenticated,
  investmentCategories,
  followCompanies,
  saveInvestmentCategory,
  deleteInvestmentCategory,
  investmentCategoriesMessage,
  isOpeningBookmark,
  handleSetIsOpeningBookmark,
  isDarkMode,
  companies,
}) => {
  const { handleToggleRead } = useNotification();
  const getStocks = (stocks) => {
    if (!stocks) {
      return null;
    }
    let parseStocks = [];
    try {
      parseStocks = JSON.parse(stocks);
    } catch (error) {
      parseStocks = JSON.parse(stocks + `"]`);
    }

    const availableStocks = parseStocks.filter((s) =>
      listStockShow.includes(s)
    );
    return availableStocks;
  };
  if (
    notification.type === trackingNotificationType.Warning &&
    ['8:30'].includes(notification.timeSend.split(' ')[1])
  ) {
    return;
  }
  if (notification.type === trackingNotificationType.newsUpdate) {
    if (
      (!notification.otherInfo?.length && !notification.items?.length) ||
      ['8:15', '8:30'].includes(notification.timeSend.split(' ')[1])
    ) {
      return;
    }
    const financialAgo = notification?.otherInfo.filter((i) => i.financialAgo);
    if (financialAgo.length) {
      return (
        <Card variant="outlined" className="w-full">
          <Box className="notification-header">
            {getICon(notification.type)}
            <div>{notification.timeSend}</div>
          </Box>
          <Box className="ml-3 notification-content">
            {notification.items.map((item, index) => (
              <div
                key={`${notification.id}-${index}`}
                className={classNames(
                  'text-sm',
                  index === notification.items.length - 1
                    ? ''
                    : ' border-b border-dashed mb-3 pb-3'
                )}
              >
                {Parser(item)}
              </div>
            ))}
          </Box>
        </Card>
      );
    }
    return (
      <Card variant="outlined" className="w-full">
        <Box className="notification-header">
          {getICon(notification.type)}
          <div className="ml-auto">{notification.timeSend}</div>
        </Box>
        <Box className="ml-3 notification-content">
          {Parser(notification.message)}
        </Box>
      </Card>
    );
  }

  if (notification.type === trackingNotificationType.Indicator) {
    return (
      <Card variant="outlined" className="w-full">
        <Box className="notification-header">
          {getICon(notification.type)}
          <div className="ml-auto">{notification.timeSend}</div>
        </Box>
        <Box className="ml-3 notification-content">
          <div className="text-sm">
            Bộ lọc của Stockup nhận thấy có một số cổ phiếu tiềm năng mà bạn có
            thể xem xét để đưa vào danh mục theo dõi của mình:
          </div>
          <ul className="ml-6 list-disc">
            {notification.otherInfo[0].stocks.map((stock) => (
              <li className="text-sm font-bold" key={stock}>
                <div className="flex items-center">
                  <span className="mr-1">{stock}</span>
                  <WrapperBookmark
                    code={stock}
                    isInvestmentCategoriesLoading={
                      isInvestmentCategoriesLoading
                    }
                    authenticated={authenticated}
                    investmentCategories={investmentCategories}
                    followCompanies={followCompanies}
                    saveInvestmentCategory={saveInvestmentCategory}
                    deleteInvestmentCategory={deleteInvestmentCategory}
                    investmentCategoriesMessage={investmentCategoriesMessage}
                    isOpeningBookmark={isOpeningBookmark}
                    handleSetIsOpeningBookmark={handleSetIsOpeningBookmark}
                  />
                </div>
              </li>
            ))}
          </ul>
        </Box>
      </Card>
    );
  }

  if (notification.type === trackingNotificationType.GENERAL) {
    const otherInfo = notification.otherInfo[0];
    return (
      <Card variant="outlined" className="w-full">
        <Box className="notification-header">
          {getICon(notification.type)}
          <div className="ml-auto">{notification.timeSend}</div>
        </Box>
        <Box className="ml-3 notification-content">
          <div className="text-sm">
            <p>
              Thị trường còn khoảng 30 phút nữa sẽ mở cửa ngày{' '}
              <b>{notification.timeSend.split(' ')[0]}</b>, Stockup tổng hợp một
              số thông tin nhà đầu tư có thể tham khảo trước khi thị trường mở
              cửa.
            </p>
            {otherInfo?.investments?.length ? (
              <div className="mt-4">
                <p className="font-bold">Danh mục đầu tư:</p>
                <ul className="list-disc ml-5">
                  {otherInfo.investments.map((investment) => (
                    <li key={investment.machungkhoan} className="mb-2">
                      {investment.change > 0 ? (
                        <p>
                          <b>{investment.machungkhoan}</b>: tăng{' '}
                          <b
                            style={{ color: getColumnColor(investment.change) }}
                          >
                            +{formatDisplayNumber(investment.change)}đ( +
                            {investment.percent}%)
                          </b>{' '}
                          trên tổng giá trị đầu tư vào cổ phiếu, nhà đầu tư nên
                          theo dõi những biến động trong phiên để có những quyết
                          định phù hợp.{' '}
                          {investment.zigZag.length &&
                          investment.percent > 4 ? (
                            <>
                              Theo dữ liệu giao dịch trong <b>12 tháng</b> gần
                              nhất chỉ có <b>{investment.zigZag.length}</b> lần{' '}
                              <b>{investment.machungkhoan}</b> tăng vượt mức{' '}
                              <b>
                                {getMinMaxZizZag(investment.zigZag)[0].toFixed(
                                  2
                                )}
                                %
                              </b>{' '}
                              theo giá đóng cửa vào các chu kỳ:
                            </>
                          ) : null}
                        </p>
                      ) : (
                        <p>
                          <b>{investment.machungkhoan}</b>: giảm{' '}
                          <b
                            style={{ color: getColumnColor(investment.change) }}
                          >
                            {formatDisplayNumber(investment.change)}đ(
                            {investment.percent}%)
                          </b>{' '}
                          và để cổ phiếu phục hồi sẽ cần tăng ít nhất{' '}
                          <b>{Math.abs(investment?.needPercent).toFixed(2)}%</b>{' '}
                          so với giá hiện tại.{' '}
                          {investment.zigZag.length &&
                          investment.percent < -4 ? (
                            <>
                              Theo dữ liệu giao dịch trong <b>12 tháng</b> gần
                              nhất chỉ có <b>{investment.zigZag.length}</b> lần{' '}
                              <b>{investment.machungkhoan}</b> tăng vượt mức{' '}
                              <b>{investment?.needPercent.toFixed(2)}%</b> theo
                              giá đóng cửa vào các chu kỳ:
                            </>
                          ) : null}
                        </p>
                      )}
                      {investment.zigZag.length &&
                      ((investment.change > 0 && investment.percent > 4) ||
                        (investment.change <= 0 && investment.percent < -4)) ? (
                        <ul className="list-disc ml-4 mt-2">
                          {investment?.zigZag?.map((zigZag) => (
                            <li key={zigZag.id}>
                              Từ{' '}
                              <b>
                                {parseDate(zigZag.fromDate)} -{' '}
                                {parseDate(zigZag.toDate)}
                              </b>{' '}
                              tăng <b>{zigZag.percent.toFixed(2)}%</b> từ{' '}
                              <b>{formatDisplayNumber(zigZag.from * 1000)}đ</b>{' '}
                              đến{' '}
                              <b>{formatDisplayNumber(zigZag.to * 1000)}đ</b>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      {investment?.news?.length ? (
                        <div className="mt-1">
                          <p className="font-bold">Tin liên quan</p>
                          <ul className="list-disc ml-6">
                            {investment.news.map((news) => (
                              <li key={news.id} className="mb-3">
                                <a
                                  href={`${ROUTES.newsStockMarket}?id=${news.aliasCode}`}
                                  target="_blank"
                                  className="hover:underline transition ease-in-out duration-200 text-primary-main"
                                  onClick={() =>
                                    handleToggleRead(
                                      ![1, 5].includes(notification.status),
                                      notification.id,
                                      notification.type
                                    )
                                  }
                                  title={news.title.replace(/&nbsp;/g, '')}
                                >
                                  {news.title.replace(/&nbsp;/g, '')}
                                </a>{' '}
                                - Nguồn {news.source}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {otherInfo?.highlight?.length ? (
              <div className="mt-4">
                <p>
                  Có một số cổ phiếu tiềm năng mà hiện chưa có trong danh mục
                  theo dõi của bạn như {renderHighlight(otherInfo.highlight)}{' '}
                  bạn có thể tham khảo và theo dõi diễn biến trong ngày.
                </p>
              </div>
            ) : null}

            {otherInfo.worldStockMarket?.length &&
            otherInfo.worldStockMarket?.[0]?.type ? (
              <div className="mt-4">
                <p className="font-bold">Thị trường chứng khoán thế giới</p>
                <ul className="mt-2 list-disc ml-5">
                  {otherInfo.worldStockMarket.map((item, index) => {
                    if (!item?.name) {
                      return null;
                    }
                    if (item?.type === 'nasdaq') {
                      return (
                        <li key={item?.id || index} className="mb-1">
                          <b>{item.name}</b>:{' '}
                          <span
                            style={{
                              color: getColumnColor(item.changePercent),
                            }}
                          >
                            {formatDisplayNumber(item.price)}{' '}
                            {formatDisplayNumber(item.change)}(
                            {item.changePercent}%)
                          </span>{' '}
                          (cập nhật lúc{' '}
                          {dayjs(Number(item.date)).format(
                            'HH:mm:ss DD/MM/YYYY'
                          )}
                          ) - Nguồn {item.source}
                        </li>
                      );
                    }
                    return (
                      <li key={item?.id || index} className="mb-1">
                        <b>{item.name}</b>:{' '}
                        <span
                          style={{ color: getColumnColor(item.changePercent) }}
                        >
                          {formatDisplayNumber(item.change)}(
                          {item.changePercent}%)
                        </span>{' '}
                        (cập nhật lúc{' '}
                        {dayjs(Number(item.date)).format('HH:mm:ss DD/MM/YYYY')}
                        ) - Nguồn {item.source}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}

            {otherInfo.commodityMarket?.length &&
            otherInfo.commodityMarket?.[0]?.type ? (
              <div className="mt-4">
                <p className="font-bold">Thị trường hàng hóa</p>
                <ul className="mt-2 list-disc ml-5">
                  {otherInfo.commodityMarket.map((item, index) => {
                    if (!item?.name) {
                      return null;
                    }
                    if (item?.type === 'gold') {
                      return (
                        <li key={item?.id || index} className="mb-1">
                          <b>{item.name}</b>:{' '}
                          <span style={{ color: getColumnColor(item.change) }}>
                            {formatDisplayNumber(item.price)}
                          </span>{' '}
                          USD/Ounce (cập nhật lúc{' '}
                          {dayjs(Number(item.date)).format(
                            'HH:mm:ss DD/MM/YYYY'
                          )}
                          ) - Nguồn {item.source}
                        </li>
                      );
                    }
                    return (
                      <li key={item?.id || index} className="mb-1">
                        <b>{item.name}</b>:{' '}
                        <span
                          style={{ color: getColumnColor(item.changePercent) }}
                        >
                          {formatDisplayNumber(item.price)}{' '}
                          {formatDisplayNumber(item.change)}(
                          {item.changePercent}%)
                        </span>{' '}
                        (cập nhật lúc{' '}
                        {dayjs(Number(item.date)).format('HH:mm:ss DD/MM/YYYY')}
                        ) - Nguồn {item.source}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}

            {otherInfo?.futuresContracts?.length &&
            otherInfo?.futuresContracts?.[0]?.type ? (
              <div className="mt-4">
                <p className="font-bold">Hợp đồng Tương Lai</p>
                <ul className="mt-2 list-disc ml-5">
                  {otherInfo.futuresContracts.map((item, index) => {
                    if (!item?.name) {
                      return null;
                    }
                    return (
                      <li key={item?.id || index} className="mb-1">
                        <b>{item.name}</b>:{' '}
                        <span
                          style={{ color: getColumnColor(item.changePercent) }}
                        >
                          {formatDisplayNumber(item.price)}{' '}
                          {formatDisplayNumber(item.change)}(
                          {item.changePercent}%)
                        </span>{' '}
                        (cập nhật lúc{' '}
                        {dayjs(Number(item.date)).format('HH:mm:ss DD/MM/YYYY')}
                        ) - Nguồn {item.source}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}

            {otherInfo.analysis[0]?.content ? (
              <div className="mt-4">
                <p className="font-bold">Dự báo xu hướng VN-Index</p>
                <p className="mt-2 ml-2">
                  {renderContentItem(JSON.parse(otherInfo.analysis[0].content))}
                </p>
              </div>
            ) : null}
          </div>
        </Box>
      </Card>
    );
  }

  if (notification.type === trackingNotificationType.GENERAL_FOLLOW) {
    const otherInfo = notification.otherInfo[0];
    return (
      <Card variant="outlined" className="w-full">
        <Box className="notification-header">
          {getICon(notification.type)}
          <div className="ml-auto">{notification.timeSend}</div>
        </Box>
        <Box className="ml-3 notification-content">
          <div>
            {otherInfo?.length ? (
              <div>
                <p className="font-bold">Danh mục theo dõi:</p>
                <div className="ml-2">
                  <div>
                    <p>
                      Có một số mã cổ phiếu trong danh mục theo dõi của bạn đang
                      có tiềm năng bạn có thể theo dõi sát hơn gồm
                    </p>
                    <ul className="list-disc ml-4 mt-2">
                      {otherInfo.map((follow) => (
                        <li key={follow.id}>
                          <b>{follow.name}</b>
                          <ul className="list-disc ml-4">
                            {follow.stocks.map((stock) => (
                              <li
                                className="mb-2"
                                key={`${follow.id}-${stock?.machungkhoan}`}
                              >
                                <p>
                                  <b>{stock?.machungkhoan}</b> giá đã giảm{' '}
                                  <b
                                    style={{
                                      color: getColumnColor(stock?.percent),
                                    }}
                                  >
                                    {stock?.percent?.toFixed(2)}%
                                  </b>{' '}
                                  so với đỉnh gần nhất.
                                </p>
                                {stock?.news?.length ? (
                                  <div className="mt-2">
                                    <p className="mb-1 font-bold">
                                      Tin liên quan:
                                    </p>
                                    <ul className="list-disc ml-6">
                                      {stock.news.map((news) => (
                                        <li key={news.id} className="mb-3">
                                          <a
                                            href={`${ROUTES.newsStockMarket}?id=${news.aliasCode}`}
                                            target="_blank"
                                            className="hover:underline transition ease-in-out duration-200 text-primary-main"
                                            onClick={() =>
                                              handleToggleRead(
                                                ![1, 5].includes(
                                                  notification.status
                                                ),
                                                notification.id,
                                                notification.type
                                              )
                                            }
                                            title={news.title.replace(
                                              /&nbsp;/g,
                                              ''
                                            )}
                                          >
                                            {news.title.replace(/&nbsp;/g, '')}
                                          </a>{' '}
                                          - Nguồn {news.source}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </Box>
      </Card>
    );
  }

  if (notification.type === trackingNotificationType.Investment) {
    const otherInfo = notification.otherInfo[0];

    return (
      <Card variant="outlined" className="w-full">
        <Box className="notification-header">
          {getICon(notification.type)}
          <div className="ml-auto">{notification.timeSend}</div>
        </Box>
        <Box className="ml-3 notification-content">
          <div className="text-sm">
            <p>
              Cổ phiếu <b>{otherInfo.stock}</b> mà bạn vừa thêm vào danh mục đầu
              tư có một số thông tin như sau:
            </p>
            <p className="font-bold my-2">Kết quả kinh doanh 4 quý gần nhất</p>
            <div>
              <FinancialChart
                financialData={otherInfo.financials}
                isDarkMode={isDarkMode}
              />
            </div>
            <p className="font-bold mt-2">
              Khối lượng giao dịch trung bình trong {otherInfo.day} ngày gần
              nhất
            </p>
            <p>
              {formatDisplayNumber(Number(otherInfo.tradingVolume).toFixed(0))}{' '}
              cổ phiếu/ngày.
            </p>
            {Number(otherInfo.tradingVolume) < 10000 ? (
              <>
                <p className="font-bold my-2"> Đáng quan tâm</p>
                <p>
                  Vì cổ phiếu <b>{otherInfo.stock}</b> có khối lượng giao dịch
                  thấp nên khi bạn muốn bán cổ phiếu này thì có thể không có quá
                  nhiều nhà đầu tư sẵn sàng mua với mức giá phù hợp. Bạn nên cân
                  nhắc khoản đầu tư này.
                </p>
              </>
            ) : null}
          </div>
        </Box>
      </Card>
    );
  }

  return (
    <ViewNotificationItem notification={notification} isDarkMode={isDarkMode} />
  );
};

const Notification = ({
  className = 'mt-5 lg:mt-0 w-full lg:w-1/3 lg:ml-3',
  info = {
    tab: 0,
    category: 0,
  },
  hideIndicator = false,
  hideInvestment = false,
  isDarkMode,
  companies,
  forceHeight = '',
}) => {
  const dispatch = useAppDispatch();
  const {
    listStockShow,
    notifications,
    isNotificationLoading,
  } = useAppSelector((state) => state.notification);
  let notificationData = transferAndGroupNotificationData(
    notifications,
    listStockShow
  );
  if (hideIndicator) {
    notificationData = notificationData.filter(
      (n) =>
        n.type !== trackingNotificationType.Indicator &&
        n.type !== trackingNotificationType.GENERAL_FOLLOW
    );
  }
  if (hideInvestment) {
    notificationData = notificationData.filter(
      (n) =>
        n.type !== trackingNotificationType.Investment &&
        n.type !== trackingNotificationType.GENERAL &&
        n.type !== trackingNotificationType.Synthetic &&
        n.type !== trackingNotificationType.priceChange &&
        n.tags.indexOf('investment') === -1
    );
  }

  const { width } = useWindowDimensions();
  const {
    isInvestmentCategoriesLoading,
    authenticated,
    investmentCategories,
    followCompanies,
    saveInvestmentCategory,
    deleteInvestmentCategory,
    investmentCategoriesMessage,
    handleSetIsOpeningBookmark,
  } = useInvestmentCategories();

  const getTitle = () => {
    return (
      <div className="flex justify-between items-center">
        <CardStyles.CardTitle>Cảnh báo/Thông báo</CardStyles.CardTitle>
        <div className="inline-flex">
          {notificationData.length >= 5 ? (
            <div className="text-center">
              <Button
                variant="text"
                onClick={() => dispatch(setOpenNotificationDrawer(info))}
              >
                Xem tất cả
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (!notifications.length) {
      return (
        <List id="warning-on-notification" className="w-full">
          <ListItem className="mb-3 text-gray-600 dark:text-gray-300">
            <div className="flex items-center">
              <div className="w-full text-gray-600 dark:text-gray-300 text-center py-5 px-0">
                <i className="ri-align-left text-3xl leading-none"></i>
                <p className="m-0 p-0 mt-1 text-sm">Không có thông báo mới</p>
              </div>
            </div>
          </ListItem>
        </List>
      );
    }

    return (
      <CustomScrollbars
        autoHeightMax={forceHeight ? forceHeight : width > 991 ? '100%' : 550}
        autoHeight={forceHeight ? true : width > 991 ? false : true}
      >
        <Styles.NotificationsSection>
          <List
            id="warning-on-notification"
            className="w-full notifications-section"
          >
            <ListItem className="mb-3 text-gray-600 dark:text-gray-300">
              {notificationData?.map((item) => (
                <NotificationStyles.NotificationItem key={item.id}>
                  <NotificationItem
                    notification={item}
                    listStockShow={listStockShow}
                    isInvestmentCategoriesLoading={
                      isInvestmentCategoriesLoading
                    }
                    authenticated={authenticated}
                    investmentCategories={investmentCategories}
                    followCompanies={followCompanies}
                    saveInvestmentCategory={saveInvestmentCategory}
                    deleteInvestmentCategory={deleteInvestmentCategory}
                    investmentCategoriesMessage={investmentCategoriesMessage}
                    isOpeningBookmark={investmentCategoriesMessage}
                    handleSetIsOpeningBookmark={handleSetIsOpeningBookmark}
                    isDarkMode={isDarkMode}
                    hideInvestment={hideInvestment}
                    companies={companies}
                  />
                </NotificationStyles.NotificationItem>
              ))}
            </ListItem>
          </List>
        </Styles.NotificationsSection>
      </CustomScrollbars>
    );
  };
  //
  return (
    <Card
      customTitle={getTitle()}
      titleClassName="flex items-center"
      className={classNames('p-4', className)}
      contentClassName={classNames(
        !notifications.length ? 'flex items-center h-[87%]' : '',
        width > 991 ? 'card-notification' : ''
      )}
      cardContentClassName="h-full"
    >
      <Loading className="white" isLoading={isNotificationLoading} />
      {renderContent()}
    </Card>
  );
};

export default Notification;
